import React, { useEffect, useState } from "react";
import Moment from "moment";

import { Button, ConfirmDialog, DetailModal, detailModalDismissButtonProps } from "../ui";

const DEFAULT_PAYMENT_DESCRIPTION = "Payment received";

function toDateInputValue(raw) {
    if (raw == null || String(raw).trim() === "") return "";
    const m = Moment(raw);
    return m.isValid() ? m.format("YYYY-MM-DD") : "";
}

function formatDateDisplay(raw) {
    if (raw == null || String(raw).trim() === "") return "—";
    const m = Moment(raw);
    return m.isValid() ? m.format("LL") : String(raw);
}

const SaleEditPaymentModal = ({
    open,
    onClose,
    payment,
    paymentStorageIndex,
    formatCurrency,
    onSavePayment,
    onRemovePayment,
}) => {
    const [isEditing, setIsEditing] = useState(false);
    const [draftPayment, setDraftPayment] = useState(null);
    const [deleteConfirmOpen, setDeleteConfirmOpen] = useState(false);

    useEffect(() => {
        if (!open) {
            setDeleteConfirmOpen(false);
        }
    }, [open]);

    useEffect(() => {
        if (!payment) {
            setDraftPayment(null);
            setIsEditing(false);
            return;
        }
        setDraftPayment({
            date: toDateInputValue(payment.date),
            amount: payment.amount ?? "",
            description:
                payment.description != null && String(payment.description).trim() !== ""
                    ? String(payment.description).trim()
                    : "",
        });
        setIsEditing(false);
    }, [payment, open]);

    if (!payment || paymentStorageIndex == null) return null;

    const handleDraftChange = (field, value) => {
        setDraftPayment((prev) => ({ ...prev, [field]: value }));
    };

    const resetDraftFromPayment = () => {
        setDraftPayment({
            date: toDateInputValue(payment.date),
            amount: payment.amount ?? "",
            description:
                payment.description != null && String(payment.description).trim() !== ""
                    ? String(payment.description).trim()
                    : "",
        });
    };

    const handleCancelEdit = () => {
        resetDraftFromPayment();
        setIsEditing(false);
    };

    const handleSaveEdit = async () => {
        await onSavePayment(paymentStorageIndex, draftPayment);
        setIsEditing(false);
    };

    const dismissModal = () => {
        if (deleteConfirmOpen) {
            setDeleteConfirmOpen(false);
            return;
        }
        onClose();
    };

    const handleConfirmRemove = () => {
        setDeleteConfirmOpen(false);
        onRemovePayment(paymentStorageIndex);
        onClose();
    };

    const descriptionDisplay = draftPayment?.description?.trim() || DEFAULT_PAYMENT_DESCRIPTION;

    const footerActions = !isEditing ? (
        <Button type="button" variant="primary" size="sm" onClick={() => setIsEditing(true)}>
            Edit
        </Button>
    ) : (
        <>
            <Button type="button" {...detailModalDismissButtonProps} onClick={handleCancelEdit}>
                Cancel
            </Button>
            <Button type="button" variant="primary" size="sm" onClick={handleSaveEdit}>
                Save
            </Button>
        </>
    );

    return (
        <DetailModal
            open={open}
            onClose={dismissModal}
            title="Payment details"
            subtitle="View or edit this payment"
            size="md"
            actions={footerActions}
            topLayer={
                <ConfirmDialog
                    nested
                    open={deleteConfirmOpen}
                    title="Delete this payment?"
                    description="This payment will be removed from the sale balance. You can record it again if needed."
                    confirmLabel="Delete"
                    cancelLabel="Cancel"
                    confirmVariant="danger"
                    onConfirm={handleConfirmRemove}
                    onCancel={() => setDeleteConfirmOpen(false)}
                />
            }
        >
            <p className="detail-section-title" style={{ marginBottom: "0.75rem" }}>
                Payment record
            </p>
            <form
                className="Sale-orderform Sale-orderform-modal"
                onSubmit={(e) => e.preventDefault()}
            >
                <div className="Sale-payment-form-row">
                    <div className="Sale-order-form-input Sale-order-form-input-date">
                        <label htmlFor="edit-payment-date">Date</label>
                        {isEditing ? (
                            <input
                                id="edit-payment-date"
                                type="date"
                                value={draftPayment?.date || ""}
                                onChange={(e) => handleDraftChange("date", e.target.value)}
                            />
                        ) : (
                            <p className="Sale-modal-readonly">{formatDateDisplay(payment.date)}</p>
                        )}
                    </div>

                    <div className="Sale-order-form-input">
                        <label htmlFor="edit-payment-description">Description</label>
                        {isEditing ? (
                            <input
                                id="edit-payment-description"
                                type="text"
                                placeholder={DEFAULT_PAYMENT_DESCRIPTION}
                                value={draftPayment?.description ?? ""}
                                onChange={(e) => handleDraftChange("description", e.target.value)}
                            />
                        ) : (
                            <p className="Sale-modal-readonly">{descriptionDisplay}</p>
                        )}
                    </div>
                </div>

                <div className="Sale-payment-amount-field">
                    <label
                        htmlFor={isEditing ? "edit-payment-amount" : "edit-payment-amount-display"}
                    >
                        Amount received
                    </label>
                    {isEditing ? (
                        <input
                            id="edit-payment-amount"
                            type="number"
                            min="0"
                            step="any"
                            placeholder="0.00"
                            value={draftPayment?.amount ?? ""}
                            onChange={(e) => handleDraftChange("amount", e.target.value)}
                            aria-describedby="edit-payment-amount-hint"
                        />
                    ) : (
                        <p
                            id="edit-payment-amount-display"
                            className="Sale-modal-readonly"
                            aria-describedby="edit-payment-amount-hint"
                        >
                            {formatCurrency(parseFloat(draftPayment?.amount))}
                        </p>
                    )}
                    <span id="edit-payment-amount-hint" className="sr-only">
                        Dollar amount for this payment
                    </span>
                </div>

                {isEditing && (
                    <div className="Sale-part-modal-delete-zone">
                        <Button
                            type="button"
                            variant="danger"
                            size="sm"
                            onClick={() => setDeleteConfirmOpen(true)}
                        >
                            Delete
                        </Button>
                    </div>
                )}
            </form>
        </DetailModal>
    );
};

export default SaleEditPaymentModal;
